import React, { useState, useEffect, useCallback } from "react";
import { ChevronDown, ChevronRight, AlertTriangle, DollarSign, ListChecks, RefreshCw, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge, PaymentBadge, CloseHint, CloseHints, STATUS } from "../components/Badges";
import { MarkPaidModal } from "../components/MarkPaidModal";
import { InvoiceCaptureModal } from "../components/InvoiceCaptureModal";
import { InlineInvoiceEdit } from "../components/InlineInvoiceEdit";
import { InlinePoDateEdit } from "../components/InlinePoDateEdit";
import { InlineQuoteEdit } from "../components/InlineQuoteEdit";
import { SendBackToLabModal, SEND_BACK_TO_LAB_STATUSES } from "../components/SendBackToLabModal";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { fmtDate } from "../lib/format";

// SPT-PTE-2026-0062 follow-up — "Move forward" / "Payment" must only offer
// options that are genuinely FORWARD from the tab you're currently viewing;
// showing every status on every tab meant "In Dispatch" offered its own
// current status (a no-op) and "Ready for Admin" (actually BACKWARDS —
// admin review happens before dispatch), which is what confused Heike.
const FORWARD_TARGETS_BY_TAB = {
  ready: ["dispatched"],
  dispatched: [],
  awaiting_payment: [],
  closed: [],
};
const PAYMENT_TARGETS_BY_TAB = {
  ready: ["awaiting_payment", "paid"],
  dispatched: ["awaiting_payment", "paid"],
  awaiting_payment: ["paid"],
  closed: [],
};
const statusLabel = (s) => STATUS[s]?.[0] || s.replace(/_/g, " ");
const PAYMENT_LABEL = { awaiting_payment: "Awaiting Payment", paid: "Mark as Paid" };

// Bulk toolbar for the "By Job (DN)" grouped view — selecting whole DN rows
// moves every line CURRENTLY SHOWN under the active tab for those DNs (never
// a job's other-status lines) via POST /dispatch/jobs/bulk-status.
function DispatchJobBulkBar({ selectedIds, sourceStatus, clearSelection, onDone }) {
  const [target, setTarget] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const apply = async () => {
    if (!target) return toast.error("Pick a target status first.");
    const label = statusLabel(target);
    if (!window.confirm(`Move all "${statusLabel(sourceStatus === "ready" ? "ready_for_delivery" : sourceStatus)}"-stage lines on ${selectedIds.length} job(s) to "${label}"?`)) return;
    setBusy(true);
    try {
      const r = (target === "awaiting_payment" || target === "paid")
        ? await api.post("/dispatch/jobs/bulk-payment", {
            job_ids: selectedIds, source_status: sourceStatus, payment_status: target, note: notes || null,
          })
        : await api.post("/dispatch/jobs/bulk-status", {
            job_ids: selectedIds, source_status: sourceStatus, target_status: target, notes: notes || null,
          });
      const skipped = (r.data.skipped || []).length;
      const pending = r.data.pending_dispatch_count || 0;
      if (pending) {
        toast.warning(`${r.data.count} line(s) marked paid · ${pending} not yet dispatched — they'll stay in "In Dispatch" until handed over, then close automatically`, { duration: 8000 });
      } else if (skipped) toast.warning(`${r.data.count} line(s) updated · ${skipped} skipped (see audit log)`);
      else toast.success(`${r.data.count} line(s) moved to "${label}"`);
      clearSelection();
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="ftabs" style={{ marginBottom: 10, alignItems: "center" }}>
      <span className="t-mut" style={{ fontSize: 12, marginRight: 4 }}>{selectedIds.length} job(s) selected</span>
      <select className="input" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="dispatch-job-bulk-status">
        <option value="">— Pick target status —</option>
        {FORWARD_TARGETS_BY_TAB[sourceStatus]?.length > 0 && (
          <optgroup label="Move forward">
            {FORWARD_TARGETS_BY_TAB[sourceStatus].map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
          </optgroup>
        )}
        {PAYMENT_TARGETS_BY_TAB[sourceStatus]?.length > 0 && (
          <optgroup label="Payment">
            {PAYMENT_TARGETS_BY_TAB[sourceStatus].map((s) => <option key={s} value={s}>{PAYMENT_LABEL[s]}</option>)}
          </optgroup>
        )}
        <optgroup label="Send back to Lab">
          {SEND_BACK_TO_LAB_STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
        </optgroup>
      </select>
      <input className="input" placeholder="Optional note (audited)" value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="dispatch-job-bulk-note"
              style={{ minWidth: 200, flex: 1 }} />
      <button className="btn gold sm" onClick={apply} disabled={busy || !target}
              data-testid="dispatch-job-bulk-apply">
        {busy ? <><RefreshCw size={13} className="spin" /> Applying…</> : <><ListChecks size={13} /> Apply to {selectedIds.length}</>}
      </button>
      <button className="btn ghost sm" onClick={() => setInvoiceOpen(true)} disabled={busy}
              data-testid="dispatch-job-bulk-invoice">
        <DollarSign size={13} /> Capture Invoice / Awaiting Payment
      </button>
      <button className="btn ghost sm" onClick={clearSelection} data-testid="dispatch-job-bulk-clear">Clear</button>
      {invoiceOpen && (
        <InvoiceCaptureModal jobIds={selectedIds} sourceStatus={sourceStatus}
          subtitle={`${selectedIds.length} job(s) selected · "${sourceStatus.replace(/_/g, " ")}" lines only`}
          onClose={() => setInvoiceOpen(false)}
          onDone={() => { setTarget(""); clearSelection(); onDone(); }} />
      )}
    </div>
  );
}

// Per-item drill-down for one Job (DN) on the Dispatch "By Job" view — built
// specifically for the "Awaiting Payment" pain point: a DN can have several
// equipment lines invoiced under different quotes/invoices/POs, so each line
// needs its own visible billing info + its own "Mark Paid" action, instead of
// hunting through the flat item list or applying one reference to everything.
function DispatchDrillTable({ jobId, items, canDispatch, status, onMarkPaid, onCaptureInvoice, onSendBackToLab, onInvoiceSaved }) {
  const showPoDate = status === "awaiting_payment";
  return (
    <table className="dt" style={{ background: "var(--card)" }} data-testid={`dispatch-drill-${jobId}`}>
      <thead>
        <tr>
          <th>Equipment</th>
          <th>Serial #</th>
          <th>Issue #</th>
          <th>Technician</th>
          <th>Status</th>
          <th>Quote</th>
          <th>Invoice</th>
          <th>PO</th>
          {showPoDate && <th>PO Received</th>}
          <th>Cert / CR #</th>
          <th>To close</th>
          <th>Action</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {items.map((it) => (
          <tr key={it.id} data-testid={`dispatch-drill-item-${it.id}`}>
            <td className="t-strong">{it.description || "—"}</td>
            <td className="mono" style={{ fontSize: 11.5 }}>{it.serial_number || "—"}</td>
            <td className="mono" style={{ fontSize: 11.5 }}>{it.issue_number || "—"}</td>
            <td className="t-mut" style={{ fontSize: 12 }}>{it.technician_name || "—"}</td>
            <td><StatusBadge status={it.quote_declined ? "quote_declined" : it.status} /></td>
            <td className="mono" style={{ fontSize: 11.5 }}>
              {canDispatch
                ? <InlineQuoteEdit item={it} onSaved={onInvoiceSaved} testidPrefix="dispatch-drill-quote" />
                : (it.quote_number || <span className="t-mut">—</span>)}
            </td>
            <td className="mono" style={{ fontSize: 11.5 }}>
              {it.status === "closed"
                ? <InlineInvoiceEdit item={it} onSaved={onInvoiceSaved} testidPrefix="dispatch-drill-invoice" />
                : (it.invoice_number || <span className="t-mut">—</span>)}
            </td>
            <td className="mono" style={{ fontSize: 11.5 }}>{it.purchase_order || <span className="t-mut">—</span>}</td>
            {showPoDate && (
              <td data-testid={`dispatch-drill-po-date-${it.id}`}>
                <InlinePoDateEdit item={it} onSaved={onInvoiceSaved} testidPrefix="dispatch-drill-po-date" />
              </td>
            )}
            <td className="mono t-mut" style={{ fontSize: 11.5 }}>{it.certificate_number || it.reserved_certificate_number || "—"}</td>
            <td>
              <CloseHints status={it.status} paymentStatus={it.payment_status}
                quote={it.quote_number} po={it.purchase_order} invoice={it.invoice_number}
                testidPrefix={`dispatch-drill-close-hint-${it.id}`} />
            </td>
            <td>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {canDispatch && ["dispatched", "delivered", "collected"].includes(it.status)
                  && !["paid", "not_required"].includes(it.payment_status) ? (
                  <button className="btn sm gold" onClick={() => onMarkPaid(it)}
                    data-testid={`dispatch-drill-mark-paid-${it.id}`}>
                    <DollarSign size={11} /> Mark Paid
                  </button>
                ) : (
                  <PaymentBadge status={it.payment_status || "not_invoiced"} />
                )}
                {canDispatch && (
                  <button className="btn sm ghost" onClick={() => onCaptureInvoice(it)}
                    data-testid={`dispatch-drill-capture-invoice-${it.id}`}>
                    <DollarSign size={11} /> Capture Invoice
                  </button>
                )}
              </div>
            </td>
            <td>
              {canDispatch && (
                <button className="btn sm ghost" onClick={() => onSendBackToLab(it)}
                  data-testid={`dispatch-drill-send-back-${it.id}`}>
                  <Undo2 size={11} /> Send back to Lab
                </button>
              )}
            </td>
          </tr>
        ))}
        {items.length === 0 && <tr><td colSpan={showPoDate ? 13 : 12} className="t-mut">No lines.</td></tr>}
      </tbody>
    </table>
  );
}

export function DispatchByJobView({ status, refreshSignal, includeLegacy, openJob, onActionApplied }) {
  const { can } = useAuth();
  const canDispatch = can("dispatch", "edit");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [expanded, setExpanded] = useState({});
  const [drillData, setDrillData] = useState({});
  const [drillBusy, setDrillBusy] = useState({});
  const [markingPaidItem, setMarkingPaidItem] = useState(null);
  const [sendingBackItem, setSendingBackItem] = useState(null);
  const [invoicingItem, setInvoicingItem] = useState(null);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => { setSelected(new Set()); }, [status]);

  useEffect(() => { const t = setTimeout(() => setDebounced(search), 300); return () => clearTimeout(t); }, [search]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/dispatch/jobs", { params: { status, hide_paid: true, search: debounced || undefined, limit: 100 } })
      .then((r) => { setRows(r.data.items || []); setTotal(r.data.total || 0); })
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  }, [status, debounced]);
  useEffect(() => { load(); }, [load, refreshSignal]);

  const loadDrill = useCallback((jobId) => {
    setDrillBusy((p) => ({ ...p, [jobId]: true }));
    return api.get("/job-items", { params: { job_id: jobId, limit: 200, sort_by: "status" } })
      .then((r) => setDrillData((p) => ({ ...p, [jobId]: r.data.items || [] })))
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setDrillBusy((p) => ({ ...p, [jobId]: false })));
  }, []);

  // A change signalled from OUTSIDE this view (e.g. a line removed/edited via
  // "Open job"'s Job Detail modal, or a Close-out) only bumps `refreshSignal`
  // — the effect above re-fetches the top-level row list (so the "Items"
  // count badge is always correct), but any ALREADY-EXPANDED drill-down
  // table was left showing its stale cached `drillData`, since `toggleExpand`
  // only fetches once per job (`if (!drillData[jobId])`). That made removed/
  // merged duplicate lines keep appearing in the expanded table even though
  // the row's own item count and the job itself were already correct.
  useEffect(() => {
    Object.keys(expanded).forEach((jobId) => { if (expanded[jobId]) loadDrill(jobId); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  const toggleExpand = (jobId) => {
    const willExpand = !expanded[jobId];
    setExpanded((p) => ({ ...p, [jobId]: willExpand }));
    if (willExpand && !drillData[jobId]) loadDrill(jobId);
  };

  const afterMarkPaid = () => {
    load();
    onActionApplied && onActionApplied();
    Object.keys(expanded).forEach((jobId) => { if (expanded[jobId]) loadDrill(jobId); });
  };

  const afterSendBackToLab = () => {
    load();
    onActionApplied && onActionApplied();
    Object.keys(expanded).forEach((jobId) => { if (expanded[jobId]) loadDrill(jobId); });
  };

  const afterCaptureInvoice = () => {
    load();
    onActionApplied && onActionApplied();
    Object.keys(expanded).forEach((jobId) => { if (expanded[jobId]) loadDrill(jobId); });
  };

  const afterBulkStatus = () => {
    load();
    onActionApplied && onActionApplied();
    Object.keys(expanded).forEach((jobId) => { if (expanded[jobId]) loadDrill(jobId); });
  };

  const toggleSelect = (jobId) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(jobId) ? next.delete(jobId) : next.add(jobId);
    return next;
  });
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));

  return (
    <div data-testid="dispatch-by-job">
      <div className="ftabs" style={{ alignItems: "center", marginBottom: 12 }}>
        <span style={{ flex: 1 }} />
        <div className="search" style={{ maxWidth: 320 }}>
          <input placeholder="Search DN, customer, quote #…" value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="dispatch-by-job-search" />
        </div>
      </div>
      {canDispatch && selected.size > 0 && (
        <DispatchJobBulkBar selectedIds={[...selected]} sourceStatus={status}
          clearSelection={() => setSelected(new Set())} onDone={afterBulkStatus} />
      )}
      <div className="tbl-wrap">
        <table className="dt">
          <thead>
            <tr>
              {canDispatch && (
                <th style={{ width: 32 }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
                    data-testid="dispatch-by-job-select-all" />
                </th>
              )}
              <th style={{ width: 32 }}></th>
              <th>Delivery Note</th>
              <th>Customer</th>
              <th style={{ width: 80, textAlign: "center" }}>Items</th>
              <th>Billing refs</th>
              <th>Technicians</th>
              <th style={{ width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="t-mut" style={{ padding: 30, textAlign: "center" }}>Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="t-mut" style={{ padding: 30, textAlign: "center" }}>No jobs match.</td></tr>
            )}
            {rows.map((r) => (
              <React.Fragment key={r.id}>
                <tr style={{ cursor: "pointer" }} onClick={() => toggleExpand(r.id)} data-testid={`dispatch-job-row-${r.id}`}>
                  {canDispatch && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)}
                        data-testid={`dispatch-job-select-${r.id}`} />
                    </td>
                  )}
                  <td>{expanded[r.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</td>
                  <td>
                    <span className="t-strong mono">{r.delivery_note_number || "—"}</span>
                    {r.mixed_billing && (
                      <span className="badge b-amber" style={{ marginLeft: 6, fontSize: 10 }}
                        title="Lines on this DN have DIFFERENT quote/invoice/PO references — check each one individually"
                        data-testid={`dispatch-job-mixed-${r.id}`}>
                        <AlertTriangle size={10} /> Mixed billing
                      </span>
                    )}
                    {(r.close_hints || []).length > 0 && (
                      <div style={{ marginTop: 4, display: "flex", gap: 4, flexWrap: "wrap" }}
                        data-testid={`dispatch-job-close-hints-${r.id}`}>
                        {r.close_hints.map((h) => <CloseHint key={h} hint={h} />)}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="t-strong">{r.customer_name || "—"}</span>
                    {r.customer_number && <span className="t-mut mono" style={{ marginLeft: 6, fontSize: 11 }}>{r.customer_number}</span>}
                    {r.purchase_orders.length === 1 && (
                      <div className="t-mut mono" style={{ fontSize: 11 }} data-testid={`dispatch-job-po-${r.id}`}>
                        PO: {r.purchase_orders[0]}{r.po_date ? ` ${fmtDate(r.po_date)}` : ""}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: "center" }}><span className="badge b-gold"><span className="bd" />{r.item_count}</span></td>
                  <td className="t-mut" style={{ fontSize: 11.5 }}>
                    {r.quote_numbers.length > 0 && <div>Q: {r.quote_numbers.join(", ")}</div>}
                    {r.invoice_numbers.length > 0 && <div>Inv: {r.invoice_numbers.join(", ")}</div>}
                    {r.purchase_orders.length > 0 && <div>PO: {r.purchase_orders.join(", ")}</div>}
                    {r.quote_numbers.length === 0 && r.invoice_numbers.length === 0 && r.purchase_orders.length === 0 && "—"}
                  </td>
                  <td className="t-mut" style={{ fontSize: 12 }}>
                    {r.technicians.length === 0 ? <i>Unassigned</i>
                      : r.technicians.length === 1 ? r.technicians[0] : `${r.technicians.length} techs`}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <button className="btn ghost sm" onClick={(e) => { e.stopPropagation(); openJob(r.id); }}
                      data-testid={`dispatch-job-open-${r.id}`}>Open job</button>
                  </td>
                </tr>
                {expanded[r.id] && (
                  <tr>
                    <td colSpan={8} style={{ padding: 0, background: "var(--card-2)" }}>
                      <div style={{ padding: "10px 14px 14px 38px" }}>
                        {drillBusy[r.id] && <div className="t-mut" style={{ fontSize: 12, padding: 12 }}>Loading items…</div>}
                        {drillData[r.id] && (
                          <DispatchDrillTable jobId={r.id} items={drillData[r.id]} canDispatch={canDispatch} status={status}
                            onMarkPaid={setMarkingPaidItem} onCaptureInvoice={setInvoicingItem} onSendBackToLab={setSendingBackItem}
                            onInvoiceSaved={() => loadDrill(r.id)} />
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="ftabs" style={{ marginTop: 10 }}>
        <span className="t-mut" style={{ fontSize: 12 }}>{total} job{total !== 1 ? "s" : ""}</span>
      </div>
      {markingPaidItem && (
        <MarkPaidModal item={markingPaidItem} onClose={() => setMarkingPaidItem(null)} onDone={afterMarkPaid} />
      )}
      {invoicingItem && (
        <InvoiceCaptureModal itemIds={[invoicingItem.id]}
          subtitle={`${invoicingItem.description || ""}${invoicingItem.serial_number ? " · SN " + invoicingItem.serial_number : ""}`}
          onClose={() => setInvoicingItem(null)} onDone={afterCaptureInvoice} />
      )}
      {sendingBackItem && (
        <SendBackToLabModal item={sendingBackItem} onClose={() => setSendingBackItem(null)} onDone={afterSendBackToLab} />
      )}
    </div>
  );
}
